import React, { useState, useEffect, useCallback, memo } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import './HeroCarousel.css';
import slide1 from '../assets/slide-1.png';
import slide2 from '../assets/slide-2.png';
import slide3 from '../assets/slide-3.png';
import slide4 from '../assets/slide-4.png';
import slide5 from '../assets/slide-5.png';

const HeroCarousel = memo(() => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [touchStart, setTouchStart] = useState(null);

  const slides = [
    {
      id: 0,
      image: slide1,
      alt: 'Cospin accelerates digital transformation for enterprises',
      title: "Accelerating Transformation",
      subtitle: "Driving Organization Efficiency",
      description: "A consulting solution built for Speed, Innovation, and Cost efficiency.",
      actions: [
        { text: "Explore Services", link: "/services", variant: "primary" },
        { text: "Get Started", link: "/contact", variant: "secondary" }
      ]
    },
    {
      id: 1,
      image: slide2,
      alt: 'Build Global Capability Centers in India with Cospin',
      title: "Build & Scale GCC",
      subtitle: "GCC as a Service",
      description: "Setup your Global Capability Center (GCC) in no time. Scale faster. Attract top talent.",
      actions: [
        { text: "Learn More", link: "/services/gcc", variant: "primary" }
      ]
    },
    {
      id: 3,
      image: slide4,
      alt: 'AI-driven innovation and practical adoption for competitive edge',
      title: "Get rid of AI FOMO",
      subtitle: "Turn Emerging Tech into Advantage",
      description: "Practical AI adoption and innovation labs for competitive edge.",
      actions: [
        { text: "Start Innovating", link: "/services/ai-innovation", variant: "primary" }
      ]
    },
    {
      id: 4,
      image: slide5,
      alt: 'Special engagements: high-intensity sprints for urgent challenges',
      title: "Special Engagements",
      subtitle: "High-Intensity Consulting Sprints",
      description: "Urgent challenges solved fast with rapid impact delivery.",
      actions: [
        { text: "Get Support", link: "/services/special-engagements", variant: "primary" }
      ]
    },
    {
      id: 2,
      image: slide3,
      alt: 'End-to-end product engineering from MVP to enterprise scale',
      title: "End-to-End Product Engineering",
      subtitle: "SaaS Platforms & Modernization",
      description: "From MVP to Enterprise SaaS that scales.",
      actions: [
        { text: "Discover More", link: "/services/product-engineering", variant: "primary" }
      ]
    }
  ];

  const handleTouchStart = (e) => {
    setTouchStart(e.touches[0].clientX);
    setIsPaused(true);
  };

  const handleTouchMove = (e) => {
    if (!touchStart) return;

    const touchEnd = e.touches[0].clientX;
    const diff = touchStart - touchEnd;

    if (Math.abs(diff) > 50) {
      if (diff > 0) {
        handleNextSlide();
      } else {
        handlePrevSlide();
      }
      setTouchStart(null);
    }
  };

  const handleTouchEnd = () => {
    setTouchStart(null);
    setIsPaused(false);
  };

  const handleMouseEnter = () => setIsPaused(true);
  const handleMouseLeave = () => setIsPaused(false);

  const handlePrevSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  }, [slides.length]);

  const handleNextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  }, [slides.length]);

  const handleSlideClick = useCallback((index) => {
    setCurrentSlide(index);
  }, []);

  useEffect(() => {
    if (!isPaused) {
      const interval = setInterval(handleNextSlide, 5000);
      return () => clearInterval(interval);
    }
  }, [isPaused, handleNextSlide]);

  return (
    <div
      className="hero-carousel"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <div className="hero__slider">
        {slides.map((slide, index) => {
          const isActive = index === currentSlide;
          const isPrev = (index === currentSlide - 1) || (currentSlide === 0 && index === slides.length - 1);

          return (
            <div
              key={slide.id}
              className={`hero__slide ${isActive ? 'active' : ''} ${isPrev ? 'prev' : ''}`}
              aria-hidden={!isActive}
            >
              <div className="hero__slide-bg">
                <img
                  src={slide.image}
                  alt={slide.alt}
                  loading={index === 0 ? "eager" : "lazy"}
                />
                {/* <div className="overlay-shapes">
                  <div className="shape shape--circle"></div>
                  <div className="shape shape--square"></div>
                  <div className="shape shape--triangle"></div>
                  <div className="shape shape--diamond"></div>
                  <div className="shape shape--line"></div>
                  <div className="shape shape--dot"></div>
                  <div className="shape shape--ring"></div>
                </div> */}
              </div>
              <div className="hero__content">
                <h2>{slide.subtitle}</h2>
                <h1>{slide.title}</h1>
                <p>{slide.description}</p>
                <div className="hero__actions">
                  {slide.actions.map((action, actionIndex) => (
                    <Link
                      key={actionIndex}
                      to={action.link}
                      className={`btn btn--${action.variant}`}
                    >
                      {action.text}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="hero__nav">
        <div className="hero__nav-content">
          <button
            className="hero__button"
            onClick={handlePrevSlide}
            aria-label="Previous slide"
          >
            <ChevronLeft aria-hidden="true" />
          </button>
          <button
            className="hero__button"
            onClick={handleNextSlide}
            aria-label="Next slide"
          >
            <ChevronRight aria-hidden="true" />
          </button>
        </div>
      </div>

      <div className="hero__progress" role="tablist">
        {slides.map((_, index) => (
          <button
            key={index}
            className={`hero__progress-item ${index === currentSlide ? 'active' : ''}`}
            onClick={() => handleSlideClick(index)}
            aria-label={`Go to slide ${index + 1}`}
            role="tab"
            aria-selected={index === currentSlide}
          />
        ))}
      </div>
    </div>
  );
});

HeroCarousel.displayName = 'HeroCarousel';

export default HeroCarousel;
